import { useEffect, useRef } from 'react';
import type { DashboardClient } from '../utils/client.utils';

type UseAddressGeocodingProps = {
  isLocationModalOpen: boolean;
  selectedClient: DashboardClient | null;
  selectedAddress: string;
  selectedLatitude: number | null;
  selectedLongitude: number | null;
  selectedState: string;
  selectedCounty: string;
  selectedZipCode: string;
  setSelectedAddress: (address: string) => void;
  setSelectedLatitude: (lat: number | null) => void;
  setSelectedLongitude: (lng: number | null) => void;
  setSelectedState: (state: string) => void;
  setSelectedCounty: (county: string) => void;
  setSelectedZipCode: (zip: string) => void;
};

export const useAddressGeocoding = ({
  isLocationModalOpen,
  selectedClient,
  selectedAddress,
  selectedLatitude,
  selectedLongitude,
  selectedState,
  selectedCounty,
  selectedZipCode,
  setSelectedAddress,
  setSelectedLatitude,
  setSelectedLongitude,
  setSelectedState,
  setSelectedCounty,
  setSelectedZipCode,
}: UseAddressGeocodingProps) => {
  const userClearedAddressRef = useRef(false);

  useEffect(() => {
    const geocodeClientZipCode = async () => {
      if (!isLocationModalOpen || !selectedClient?.profile) return;
      // Don't auto-fill if user manually cleared the address
      if (userClearedAddressRef.current) {
        return;
      }
      // Don't auto-fill if address is already set (user has entered something)
      if (selectedAddress && selectedAddress.trim() !== '') {
        return;
      }
      if (selectedAddress && selectedLatitude && selectedLongitude) {
        return;
      }
      const clientZip = selectedClient.profile.location?.zip || '';
      const clientCity = selectedClient.profile.location?.city || '';
      const clientState = selectedClient.profile.location?.state || '';
      if (!clientZip) {
        return;
      }
      if (!selectedAddress || selectedAddress.trim() === '') {
        if (clientCity && clientState && clientZip) {
          setSelectedAddress(`${clientCity}, ${clientState} ${clientZip}`);
        } else {
          setSelectedAddress(clientZip);
        }
      }
      if (!selectedLatitude || !selectedLongitude) {
        try {
          if (window.google && window.google.maps) {
            const geocoder = new google.maps.Geocoder();
            const result = await geocoder.geocode({ address: clientZip });
            if (result.results && result.results.length > 0) {
              const place = result.results[0];
              const latitude = place.geometry.location.lat();
              const longitude = place.geometry.location.lng();
              let state = clientState || '';
              let county = '';
              let zipCode = clientZip;
              if (place.address_components) {
                place.address_components.forEach(component => {
                  const types = component.types;
                  const name = component.long_name;
                  if (types.includes("administrative_area_level_1") && !state) {
                    state = name;
                  }
                  if (types.includes("administrative_area_level_2")) {
                    county = name;
                  }
                  if (types.includes("postal_code")) {
                    zipCode = name;
                  }
                });
              }
              setSelectedLatitude(latitude);
              setSelectedLongitude(longitude);
              if (!selectedState) setSelectedState(state);
              if (!selectedCounty) setSelectedCounty(county);
              if (!selectedZipCode) setSelectedZipCode(zipCode);
            }
          }
        } catch (error) {
          if (!selectedZipCode) setSelectedZipCode(clientZip);
          if (!selectedState && clientState) {
            setSelectedState(clientState);
          }
        }
      }
    };
    geocodeClientZipCode();
  }, [isLocationModalOpen, selectedClient, selectedAddress, selectedLatitude, selectedLongitude, selectedState, selectedCounty, selectedZipCode, setSelectedAddress, setSelectedLatitude, setSelectedLongitude, setSelectedState, setSelectedCounty, setSelectedZipCode]);

  return {
    userClearedAddressRef,
  };
};

